/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, all, takeEvery, select, call, delay, fork, join } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ActionRequest,
  ActionSuccess,
  ActionFail,
  XError,
  TimeUtils,
  Configs,
  Menu,
  MenuUiArea,
  MenuErrors,
  ProductPriceApi,
  Specs,
  FileFormatToFileTypeMap,
  Constants,
  AssetsApi,
  Channel,
  Region,
  ProductPrice,
  ParentalControlSettings,
  HideUnsubscribed,
  Ducks,
  LocalStorage,
  ChannelStorage,
  AssetUtils,
  ChannelsApi,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import { Selectors as RegionSelectors } from '../region';
import { Selectors as ContentSettingsSelectors } from '../contentSettings';
import IFDebug from '../../../configs/IFDebug';
import AppConfig from '../../../../services/AppConfig';

export enum ActionTypes {
  REQUEST = 'channels/REQUEST',
  SUCCESS = 'channels/SUCCESS',
  FAIL = 'channels/FAIL',
}

export interface LoadAssetsLicenseDataMeta {
  channelIndex?: number;
}

type Request = ActionRequest<ActionTypes.REQUEST>;
type Success = ActionSuccess<ActionTypes.SUCCESS, Channel[]>;
type Fail = ActionFail<ActionTypes.FAIL>;

export type Actions = Request | Success | Fail;

const Actions = {
  request: (): Request => ({ type: ActionTypes.REQUEST }),
  success: (payload: Channel[]): Success => ({ type: ActionTypes.SUCCESS, payload }),
  fail: (error: XError): Fail => ({ type: ActionTypes.FAIL, error }),
};

export const Creators = {
  loadChannels: (): Request => Actions.request(),
};

export interface ReducerState {
  data?: Channel[];
  processing?: boolean;
  processed?: boolean;
  error?: XError;
}

const initialState: ReducerState = {};

export default function channelsReducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionTypes.SUCCESS: {
      return {
        data: action.payload || [],
        processing: false,
        processed: true,
      };
    }
    case ActionTypes.FAIL:
      return {
        ...state,
        processing: false,
        processed: true,
        error: action.error,
      };
    case ActionTypes.REQUEST:
      return {
        ...state,
        processing: true,
        processed: false,
      };
    default:
      return state;
  }
}

// SELECTORS
const PAGINATED_CHANNELS_NAMESPACE = 'CHANNELS';

export const Selectors = {
  isProcessed: (state: StoreState): boolean => state.channels.processed === true,
  getError: (state: StoreState): XError | undefined => state.channels.error,
  getChannels: (state: StoreState): Channel[] | undefined => {
    return state.channels?.data;
  },
  getChannelByEpgId: (state: StoreState, epgId: number): Channel | undefined => {
    return state.channels?.data?.find((channel) => channel.epgId === epgId);
  },
  getChannelById: (state: StoreState, id: number): Channel | undefined => {
    return state.channels?.data?.find((channel) => channel.id === id);
  },
  getChannelIndexByEpgId: (state: StoreState, epgId: number | undefined): number | undefined => {
    return epgId !== undefined ? state.channels?.data?.findIndex((channel) => channel.epgId === epgId) : undefined;
  },
};

// CACHE

function* saveChannelsToCache(channels: Channel[]): SagaIterator {
  if (channels && channels.length) {
    const channelStorage = {
      channels,
      timestamp: TimeUtils.now(),
    };
    yield call(LocalStorage.storeChannels, channelStorage);
  }
}

function* isChannelsCacheValid(channelStorage: ChannelStorage): SagaIterator<boolean> {
  const { channels, timestamp } = channelStorage;
  if (channels && channels.length && timestamp) {
    const configs: Configs | undefined = yield select(Ducks.Dms.Selectors.getDmsConfigs);
    if (configs) {
      const expirationDate = TimeUtils.addHours(timestamp, configs.entitlementUpdate);
      if (TimeUtils.isAfterNow(expirationDate)) {
        return true;
      }
    }
  }

  return false;
}

function* getChannelsFromCache(): SagaIterator {
  try {
    const channelStorage: ChannelStorage | undefined = yield call(LocalStorage.getChannels);

    if (channelStorage && (yield call(isChannelsCacheValid, channelStorage))) {
      return channelStorage.channels;
    }

    return undefined;
  } catch {
    return undefined;
  }
}

// SAGAS
function* getChannelsMenuItemId(): SagaIterator {
  const menuAlreadyCalled = yield select(Ducks.Menu.Selectors.isProcessed);
  if (menuAlreadyCalled) {
    const menu: Menu = yield select(Ducks.Menu.Selectors.getMenu);
    if (menu) {
      const channelsMenuItem = menu[MenuUiArea.EPG];
      if (channelsMenuItem) {
        if (channelsMenuItem.channelId) {
          return channelsMenuItem.channelId;
        }
      }
      throw XError.fromCode(MenuErrors.validation.missingMenuItemTvGuide);
    } else {
      throw new Error('Channels saga: Menu is undefined');
    }
  } else {
    throw new Error('Channels saga called before Menu');
  }
}

function* productPriceList(fileIds: number[]): SagaIterator {
  return yield Ducks.Api.callApi(ProductPriceApi.listByFileIds, fileIds);
}

function* getChannelEntitlements(channels: Channel[]): SagaIterator {
  let fileIds: number[] = [];
  let productPriceArray: ProductPrice[] = [];
  const tasks = [];

  if (channels) {
    // Disable restriction in for...of usage so yield can be used in the cicle
    /* eslint-disable-next-line no-restricted-syntax */
    for (const channel of channels) {
      /* eslint-disable-next-line no-restricted-syntax */
      for (const stream of channel.streams) {
        fileIds.push(stream.id);
        if (fileIds.length === Specs.PRODUCT_PRICE_LIST_MAX_CHUNK) {
          tasks.push(yield fork(productPriceList, fileIds));
          fileIds = [];
        }
      }
    }
    if (fileIds.length) {
      tasks.push(yield fork(productPriceList, fileIds));
    }

    const tasksResults = yield join(tasks);
    productPriceArray = [].concat(...tasksResults);
  }
  return productPriceArray;
}

function updateChannelsWithEntitlements(channels: Channel[], productPriceArray: ProductPrice[]): Channel[] {
  const productPriceMap = new Map(productPriceArray.map((productPrice) => [productPrice.fileId, productPrice]));
  if (channels) {
    channels.forEach((channel) => {
      const channelRef = channel;
      channelRef.streams.forEach((stream, idx) => {
        const streamRef = stream;
        const productPriceElement = productPriceMap.get(streamRef.id);
        if (productPriceElement) {
          streamRef.isSubscribed = AssetUtils.isProductPricePurchased(productPriceElement);
        }

        channelRef.isSubscribed = channelRef.isSubscribed || streamRef.isSubscribed;
        channelRef.streams[idx] = streamRef;
      });
    });
  }

  return channels;
}

function* getChannelsFromApi(channelId: number): SagaIterator {
  yield Ducks.Pagination.clearPaginatedApi(PAGINATED_CHANNELS_NAMESPACE);
  const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
  const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

  const result: Channel[] = yield Ducks.Pagination.callCompletedPaginatedApi(
    PAGINATED_CHANNELS_NAMESPACE,
    Constants.CHANNELS_API_PAGE_SIZE,
    ChannelsApi.channelLineup,
    channelId,
    filesFormat,
    parentalControlSettings,
    HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY]
  );
  return result;
}

function* removeChannelsNotInRegion(channels: Channel[]): SagaIterator {
  if (yield select(RegionSelectors.isEnabled)) {
    const region: Region = yield select(RegionSelectors.getRegion);

    const regionChannelMap = new Map(region.channels.map((channel) => [channel.id, channel]));

    return channels.filter((channel) => regionChannelMap.has(channel.id));
  }

  return channels;
}

function* removeUnsubscribedChannelsIfHidden(channels: Channel[]): SagaIterator {
  const filteredChannels: Channel[] = [];
  if (channels) {
    const showUnsubscribedChannels: boolean = yield select(ContentSettingsSelectors.getShowUnsubscribedChannels);
    const isConverged: boolean = yield select(Ducks.Household.Selectors.data.getIsConverged);

    channels.forEach((channel) => {
      if (channel.isSubscribed || (showUnsubscribedChannels && (isConverged || !channel.hideUnsubscribed))) {
        filteredChannels.push(channel);
      }
    });
  }

  return filteredChannels;
}

function sortChannels(channels: Channel[]): Channel[] {
  channels.sort((channelA, channelB) => {
    return channelA.channelNumber - channelB.channelNumber;
  });
  return channels;
}

function* loadChannels(skipCache = false): SagaIterator {
  try {
    let channels: Channel[] = skipCache ? undefined : yield call(getChannelsFromCache);
    if (!channels) {
      const channelId: number = yield call(getChannelsMenuItemId);
      channels = yield call(getChannelsFromApi, channelId);
      channels = yield call(removeChannelsNotInRegion, channels);
      const productPriceArray: ProductPrice[] = yield call(getChannelEntitlements, channels);
      yield put(Ducks.Currency.ActionCreators.update(productPriceArray));
      channels = updateChannelsWithEntitlements(channels, productPriceArray);
      channels = yield call(removeUnsubscribedChannelsIfHidden, channels);
      channels = sortChannels(channels);
      yield call(saveChannelsToCache, channels);
    }
    yield put(Actions.success(channels));
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

function* scheduleChannelsUpdate(): SagaIterator {
  const configs: Configs | undefined = yield select(Ducks.Dms.Selectors.getDmsConfigs);
  if (configs) {
    while (true) {
      yield delay(TimeUtils.hoursToMilliseconds(configs.maxEntitlementUpdate));
      yield call(loadChannels, true);
    }
  }
}

function* checkRegionChange(): SagaIterator {
  const region: Region = yield select(RegionSelectors.getRegion);
  return region.changed;
}

function* initChannelsRequestSaga(): SagaIterator {
  const refreshChannels: boolean = (yield select(RegionSelectors.isEnabled)) && (yield call(checkRegionChange));
  yield call(loadChannels, refreshChannels || IFDebug.SKIP_CHANNELS_CACHE);
  yield call(scheduleChannelsUpdate);
}

export function* getChannelFromStorage(): SagaIterator<number | undefined> {
  return yield call(LocalStorage.getTunedChannel);
}

export function* getPreviousChannelFromStorage(): SagaIterator<number | undefined> {
  const epgIdList: number[] | undefined = yield call(LocalStorage.getTunedChannelHistory);
  return epgIdList && epgIdList.length >= 2 ? epgIdList[1] : undefined;
}

export function* setChannelToStorage(channelEpgId: number): SagaIterator {
  yield call(LocalStorage.storeTunedChannel, channelEpgId);
}

export function* updateChannels(): SagaIterator {
  yield call(loadChannels, true);
}

export function* saga(): SagaIterator {
  yield all([takeEvery(ActionTypes.REQUEST, initChannelsRequestSaga)]);
}
